import { useRef, useState } from "react";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Lightbulb, FileText, Network, ListOrdered, Bot, ShieldCheck, Rocket, RefreshCcw, Check } from "lucide-react";
import { Reveal } from "@/components/fx";

const STEPS = [
  { t: "Idea", d: "One sentence, typed or spoken.", I: Lightbulb },
  { t: "Product Brief", d: "Users, roles, rules, data — written for you, editable inline.", I: FileText },
  { t: "Architecture & System Map", d: "Every screen, service and data flow mapped before any code.", I: Network },
  { t: "Build Plan", d: "Numbered steps you approve. Nothing runs without your yes.", I: ListOrdered },
  { t: "Agent Build", d: "Specialist agents write the app in parallel.", I: Bot },
  { t: "Quality Engine", d: "Syntax, wiring, CRUD smoke tests, rule probes, headless render, security scan.", I: ShieldCheck },
  { t: "Deploy", d: "A live preview with a real data backend, versioned.", I: Rocket },
  { t: "Run & Evolve", d: "Ask for changes; FORGE re-plans, rebuilds and re-proves.", I: RefreshCcw },
];

const Visual = ({ i }) => {
  const common = { initial: { opacity: 0, scale: 0.96 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0 }, transition: { duration: 0.35 } };
  if (i === 1) return (
    <motion.div {...common} className="space-y-2">
      {["App name · Fade & Co.", "Roles · customer, barber, owner", "Rule · no double-booking", "Entities · bookings, barbers"].map((x, k) => (
        <motion.div key={x} initial={{ width: 0, opacity: 0 }} animate={{ width: "100%", opacity: 1 }} transition={{ delay: k * 0.25 }} className="overflow-hidden whitespace-nowrap rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80">{x}</motion.div>
      ))}
    </motion.div>
  );
  if (i === 2) {
    const nodes = [[40, 40], [200, 30], [120, 120], [260, 130], [40, 170]];
    return (
      <motion.svg {...common} viewBox="0 0 300 200" className="h-48 w-full">
        {[[0, 2], [1, 2], [2, 3], [2, 4], [1, 3]].map(([a, b], k) => (
          <motion.line key={k} x1={nodes[a][0]} y1={nodes[a][1]} x2={nodes[b][0]} y2={nodes[b][1]} stroke="#7a5cff" strokeWidth="1.5" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.3 + k * 0.2, duration: 0.6 }} />
        ))}
        {nodes.map(([x, y], k) => <motion.circle key={k} cx={x} cy={y} r="11" fill="#13101f" stroke={["#22d3ee", "#ff5c7a", "#7a5cff", "#6fd6b0", "#22d3ee"][k]} strokeWidth="2" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: k * 0.12 }} />)}
      </motion.svg>
    );
  }
  if (i === 5 || i === 3) return (
    <motion.ul {...common} className="space-y-1.5">
      {(i === 5 ? ["JS syntax · node --check", "Wiring · refs + collections", "Smoke CRUD per collection", "Rule probe · overlap → 422", "Headless render · 0 errors", "Secrets scan · clean"] : ["1 · Screens & navigation", "2 · Collections & seed data", "3 · Frontend files", "4 · Server rules", "5 · Checks & deploy"]).map((x, k) => (
        <motion.li key={x} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: k * 0.18 }} className="flex items-center gap-2 text-sm text-white/80">
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2 + k * 0.18 }} className="grid h-5 w-5 place-items-center rounded-full bg-[#6fd6b0]/20 text-[#6fd6b0]"><Check className="h-3 w-3" /></motion.span>{x}
        </motion.li>
      ))}
    </motion.ul>
  );
  const S = STEPS[i];
  return (
    <motion.div {...common} className="grid h-48 place-items-center">
      <motion.div animate={{ rotate: [0, 6, -6, 0], scale: [1, 1.06, 1] }} transition={{ repeat: Infinity, duration: 3 }} className="grid h-24 w-24 place-items-center rounded-3xl bg-fire glow-coral">
        <S.I className="h-10 w-10 text-white" />
      </motion.div>
    </motion.div>
  );
};

export const HowItWorks = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 40%"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });
  return (
    <section id="how" data-testid="how-it-works-section" className="relative mx-auto max-w-7xl px-6 py-16 md:py-20">
      <Reveal><span className="label-mono text-[#22d3ee]">How it works</span></Reveal>
      <Reveal delay={0.05}><h2 className="font-display mt-3 max-w-3xl text-3xl font-bold text-white md:text-5xl">From one sentence to a proven, running product.</h2></Reveal>
      <div ref={ref} className="mt-14 grid gap-10 lg:grid-cols-[1fr_1.1fr]">
        <div className="relative pl-8">
          <div className="absolute left-3 top-0 h-full w-px bg-white/10" />
          <motion.div style={{ scaleY }} className="absolute left-3 top-0 h-full w-px origin-top bg-gradient-to-b from-[#22d3ee] via-[#7a5cff] to-[#ff5c7a]" />
          {STEPS.map((s, i) => (
            <button key={s.t} data-testid={`how-step-${i}`} onClick={() => setActive(i)} className={`relative mb-3 flex w-full items-start gap-4 rounded-2xl border p-4 text-left transition-colors duration-300 ${active === i ? "border-[#7a5cff]/60 bg-[#7a5cff]/10" : "border-white/5 hover:border-white/15 hover:bg-white/[0.03]"}`}>
              <span className={`absolute -left-[26px] top-6 h-3 w-3 rounded-full border-2 ${active === i ? "border-[#22d3ee] bg-[#22d3ee]" : "border-white/30 bg-[#07060b]"}`} />
              <s.I className={`mt-0.5 h-5 w-5 shrink-0 ${active === i ? "text-[#22d3ee]" : "text-white/40"}`} />
              <div>
                <div className="font-display font-semibold text-white"><span className="label-mono mr-2 text-white/35">{String(i + 1).padStart(2, "0")}</span>{s.t}</div>
                <div className="mt-1 text-sm text-white/55">{s.d}</div>
              </div>
            </button>
          ))}
        </div>
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div data-testid="how-step-visual" className="glass noise relative overflow-hidden rounded-3xl p-8">
            <div className="label-mono text-white/40">Step {active + 1} / {STEPS.length}</div>
            <div className="font-display mt-2 text-2xl font-bold text-white">{STEPS[active].t}</div>
            <div className="mt-6 min-h-[200px]"><AnimatePresence mode="wait"><Visual key={active} i={active} /></AnimatePresence></div>
          </div>
        </div>
      </div>
    </section>
  );
};
